import React, {
  useContext,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import classNames from 'classnames';
import DetailPageField from './components/DetailPageField';
import DropzoneField from 'components/DropzoneField';
import InputField from 'components/InputField';
import SelectField from 'components/SelectField';
import useOperationStepValues from 'router/data/useOperationStepValues';
import { Listing } from 'models/Listing';
import { useTranslation } from 'react-i18next';
import TextareaArrayField from 'components/TextareaArrayField';
import HighlightsIcon from 'icons/Highlights.icon';
import ProductsIcon from 'icons/Products.icon';
import KeyAssetsIcon from 'icons/KeyAssets.icon';
import { Form } from 'react-final-form';
import ListingContext from '../../providers/Listing/Listing.context';
import TextAreaField from 'components/TextAreaField';
import { Button } from 'ncoded-component-library';
import FloatingActions from 'components/FloatingActions';
import { useLocation, useNavigate } from 'react-router-dom';
import api from 'api';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import utils, {
  checkIsUrgent,
  getAcceptedListingContacts,
  getCreateListingRoute,
  sentryError,
} from 'utils';
import {
  DESCRIPTION_MAX_CHARACTERS,
  HIGHLIGHTS_MAX_CHARACTERS,
  TITLE_MAX_CHARACTERS,
  LISTING_CONTACT_FIRM_LOGO_FILE_NAME,
  checkIfTeaserIsEnabled,
  processLCWValuesBeforeSubmit,
} from '../../utils';
import arrayMutators from 'final-form-arrays';
import ListingEditContext from '../../subrouters/EditListingSubrouter/providers/Listing/Listing.context';
import { SLUG_FIELD_NAME } from '../../components/ListingSlugField/ListingSlugField.component';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import StarIcon from 'icons/Star.icon';
import ChatIcon from 'icons/Chat.icon';
import ShareIcon from 'icons/Share.icon';
import ListingPrices from 'components/ListingPrices';
import LocationmarkerIcon from 'icons/Locationmarker.icon';
import EoiDate from 'components/EoiDate';
import { ReactComponent as LinkedInIcon } from 'icons/linkedin.svg';
import ResolveImage from 'components/ResolveImage';
import { YesNo } from 'models/Fields';
import useLocationMapper from 'router/data/useLocationMapper';
import {
  EDIT_ROUTE,
  LCW_ROUTE_PATHS,
  LCW_STATEMENT_OF_INFORMATION_ROUTE,
  NEW_WINDOW_PROPS,
} from 'data/global';
import ListingFieldsRequiredVisibleInfoContext from '../../providers/ListingFieldsRequiredVisibleInfo/ListingFieldsRequiredVisibleInfo.context';

import './ListingDetailsPreview.styles.scss';
import './ListingDetailsPreview.styles.responsive.scss';

type ListingDetailsPreviewProps = {
  className?: string;
};

const ListingDetailsPreview: React.FC<ListingDetailsPreviewProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isEdit = pathname.includes(EDIT_ROUTE);
  const { isSuperAdmin } = useContext(CurrentUserContext);

  const ListingCreateContextValues = useContext(ListingContext);
  const ListingEditContextValues = useContext(ListingEditContext);
  const currentValues = useRef<Partial<Listing>>(null);

  const {
    listingFormValues,
    listingType,
    listing: {
      _id: listingId,
      stepsCompleted,
      stockAtValue,
      askingPriceSelect,
      amount,
      amountFrom,
      amountTo,
      revenue,
      EBITDA,
      averageStockValue,
      seekingExpressionOfInterestDate,
      dealRoomId,
      listingContacts,
      currency,
    },
    setListing,
  } = isEdit ? ListingEditContextValues : ListingCreateContextValues;

  const { fields } = useContext(ListingFieldsRequiredVisibleInfoContext);

  const {
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    revenueRequiredVisibleInfo,
    EBITDARequiredVisibleInfo,
    averageStockValueRequiredVisibleInfo,
  } = fields || {};

  const { industrySelectOptions, locationOptions } = useOperationStepValues();
  const { locationLabelMapper } = useLocationMapper();

  const navigate = useNavigate();
  const { popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('listing-details-preview', className);

  const acceptedListingContacts = useMemo(
    () => getAcceptedListingContacts(listingContacts),
    [listingContacts],
  );

  // PATCH LISTING ON PREVIEW MODE PAGE
  const patchRequest = useCallback(
    async (
      values: Partial<Listing>,
      onSuccess?: (listing: Listing) => void,
    ) => {
      const {
        mediaPackage,
        controlledInformation,
        appointmentType,
        someoneToReplaceOwner,
        hasCommercialProperty,
        commercialProperty,
        enableTeaser,
        teaserFile,
        EBITDAMultiple,
        EBITDA,
        accountingPeriod,
        buyerOutreach,
        contractedCustomers,
        saleType,
        isEnterprise,
        ...restValues
      } = values;

      const processedValues = processLCWValuesBeforeSubmit(restValues);

      const patchValues = utils.changePropertyValueWith(
        {
          hasCommercialProperty,
          accountingPeriod,
          ...restValues,
          ...processedValues,
          ...(stepsCompleted === 0 ? { type: listingType } : {}),
          ...(stepsCompleted === 0 && checkIsUrgent(listingType)
            ? { appointmentType }
            : {}),
          ...(controlledInformation ? { controlledInformation } : {}),
          ...(hasCommercialProperty ? { commercialProperty } : {}),
          ...(accountingPeriod
            ? { EBITDAMultiple: EBITDAMultiple || null }
            : {}),
          ...(stepsCompleted === 3
            ? {
                mediaPackage: mediaPackage || null,
                buyerOutreach: buyerOutreach || null,
              }
            : {}),
          ...(someoneToReplaceOwner
            ? {
                someoneToReplaceOwner: someoneToReplaceOwner === YesNo.Yes,
              }
            : {}),
          ...(contractedCustomers
            ? {
                contractedCustomers: contractedCustomers === YesNo.Yes,
              }
            : {}),
          ...(saleType && {
            saleType,
          }),
          ...(checkIfTeaserIsEnabled(values) ? { enableTeaser } : {}),
        },
        null,
        undefined,
      );

      try {
        const {
          data: { listing },
        } = await api.listings.patchListing(listingId, patchValues);
        setListing(listing);
        currentValues.current = null;
        onSuccess?.(listing);
      } catch (e) {
        sentryError(e);
      }
    },
    [listingId, listingType, setListing, stepsCompleted],
  );

  const onSubmit = useCallback(
    async (values) =>
      patchRequest(values, (listing: Listing) =>
        navigate(
          `../${
            isEdit
              ? LCW_STATEMENT_OF_INFORMATION_ROUTE
              : LCW_ROUTE_PATHS[listing.currentStep ?? listing.stepsCompleted]
          }`,
          {
            replace: true,
          },
        ),
      ),
    [isEdit, navigate, patchRequest],
  );

  const uploadListingGallery: DropzoneUpload = useCallback(
    async (file, _, config, customName) => {
      try {
        const { uploadedFile, listing } = await api.listings.uploadFile(
          file,
          'gallery',
          listingId,
          config,
          customName,
        );
        setListing(listing);
        return uploadedFile;
      } catch (e) {
        popServerError(e);
      }
    },
    [listingId, popServerError, setListing],
  );

  const deleteListingGallery: DropzoneDelete = useCallback(
    async (file, _) => {
      try {
        const {
          data: { fileId, listing },
        } = await api.listings.deleteFile(listingId, 'gallery', file.id);
        setListing(listing);
        return fileId;
      } catch (e) {
        popServerError(e);
      }
    },
    [listingId, popServerError, setListing],
  );

  const uploadCompanyLogo: DropzoneUpload = useCallback(
    async (file, _, config) => {
      const { uploadedFile, listing } = await api.listings.uploadFile(
        file,
        LISTING_CONTACT_FIRM_LOGO_FILE_NAME,
        listingId,
        config,
      );
      setListing(listing);
      return uploadedFile;
    },
    [listingId, setListing],
  );

  const deleteCompanyLogo: DropzoneDelete = useCallback(
    async (file, _) => {
      const {
        data: { fileId, listing },
      } = await api.listings.deleteListingContact(
        LISTING_CONTACT_FIRM_LOGO_FILE_NAME,
        file.id,
        listingId,
      );
      setListing(listing);
      return fileId;
    },
    [listingId, setListing],
  );

  useEffect(() => {
    return () => {
      currentValues.current && patchRequest?.(currentValues.current);
    };
  }, [patchRequest]);

  const ActionsPlaceholder = useMemo(
    () => (
      <div className="listing-actions">
        <Button variant="outline" icon={ChatIcon}>
          {t('enquire')}
        </Button>
        <Button variant="link" icon={StarIcon}>
          {t('shortlist')}
        </Button>
        <Button variant="link" icon={ShareIcon}>
          {t('share')}
        </Button>
      </div>
    ),
    [t],
  );

  return (
    <Form<Partial<Listing>>
      keepDirtyOnReinitialize
      onSubmit={onSubmit}
      initialValues={listingFormValues}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, values }) => {
        currentValues.current = values;

        const locationLabel = locationLabelMapper[values.location?.state];

        return (
          <form onSubmit={handleSubmit} className={classes}>
            <div className="form-content">
              <div>
                {isSuperAdmin && (
                  <DetailPageField
                    name={SLUG_FIELD_NAME}
                    previewType="slug"
                    placeholder={t('listingTitleSlug')}
                  />
                )}
              </div>
              <span className="spacing" />

              <div className="image-cont">
                <DetailPageField
                  name="gallery"
                  previewType="images"
                  field={{
                    component: DropzoneField,
                    uploadRequest: uploadListingGallery,
                    deleteRequest: deleteListingGallery,
                    accept: 'image/*',
                  }}
                />
              </div>
              <div className="listing-details-preview__main">
                <div className="title-wrapper">
                  <DetailPageField
                    name="listingTitle"
                    previewType="h1"
                    placeholder={t('listingTitle')}
                    field={{
                      component: InputField,
                      maxLength: TITLE_MAX_CHARACTERS,
                    }}
                  />
                </div>
                <div className="listing-details-preview__main__main-info-actions">
                  {!checkIsUrgent(listingType) ? (
                    <DetailPageField
                      name={'location.state' as keyof Listing}
                      previewType="link"
                      placeholder={t('state')}
                      label={locationLabel}
                      field={{
                        component: SelectField,
                        options: locationOptions,
                        renderAsPortal: true,
                        portalClassName: 'state-select-portal',
                      }}
                    />
                  ) : (
                    <>
                      <LocationmarkerIcon />
                      <DetailPageField
                        name="locationPostcode"
                        previewType="link"
                        placeholder={t('postcode')}
                        field={{
                          component: InputField,
                          type: 'number',
                        }}
                      />
                    </>
                  )}

                  <DetailPageField
                    name="industry"
                    previewType="link"
                    placeholder={t('industry')}
                    field={{
                      component: SelectField,
                      options: industrySelectOptions,
                      renderAsPortal: true,
                      searchable: true,
                    }}
                  />
                </div>
                <EoiDate
                  bigger
                  seekingExpressionOfInterestDate={
                    seekingExpressionOfInterestDate
                  }
                />
                <ListingPrices
                  isPreview
                  askingPrice={askingPriceSelect}
                  amount={amount}
                  amountFrom={amountFrom}
                  amountTo={amountTo}
                  revenue={revenue}
                  EBITDA={EBITDA}
                  averageStockValue={averageStockValue}
                  stockAtValue={stockAtValue}
                  currency={currency}
                  amountRequiredVisibleInfo={amountRequiredVisibleInfo}
                  amountFromRequiredVisibleInfo={amountFromRequiredVisibleInfo}
                  amountToRequiredVisibleInfo={amountToRequiredVisibleInfo}
                  revenueRequiredVisibleInfo={revenueRequiredVisibleInfo}
                  EBITDARequiredVisibleInfo={EBITDARequiredVisibleInfo}
                  averageStockValueRequiredVisibleInfo={
                    averageStockValueRequiredVisibleInfo
                  }
                />
                <div className="listing-details-preview__main__description">
                  <DetailPageField
                    name="businessDescription"
                    previewType="p"
                    placeholder={t('description')}
                    field={{
                      component: TextAreaField,
                      maxLength: DESCRIPTION_MAX_CHARACTERS,
                      autoResize: true,
                    }}
                    allowEnter
                  />
                </div>
                {ActionsPlaceholder}
                <div className="listing-details-preview__main__highlights">
                  <DetailPageField
                    name="highlights"
                    previewType="highlightlsSection"
                    field={{
                      component: TextareaArrayField,
                      fieldLabel: t('highlight'),
                      maxFieldLength: HIGHLIGHTS_MAX_CHARACTERS,
                    }}
                    icon={HighlightsIcon}
                    placeholder={t('highlight')}
                  />
                  <DetailPageField
                    name="products"
                    previewType="highlightlsSection"
                    field={{
                      component: TextareaArrayField,
                      fieldLabel: t('product'),
                      maxFieldLength: HIGHLIGHTS_MAX_CHARACTERS,
                    }}
                    icon={ProductsIcon}
                    placeholder={t('product')}
                  />
                  <DetailPageField
                    name="keyAssets"
                    previewType="highlightlsSection"
                    field={{
                      component: TextareaArrayField,
                      fieldLabel: t('keyAsset'),
                      maxFieldLength: HIGHLIGHTS_MAX_CHARACTERS,
                    }}
                    icon={KeyAssetsIcon}
                    placeholder={t('keyAsset')}
                  />
                </div>
              </div>
              <aside>
                <div className="resolve-agent-details">
                  <p className="title">{t('presentedBy')}</p>
                  <DetailPageField
                    name={'listingContact.firmLogo' as keyof Listing}
                    previewType="images"
                    field={{
                      component: DropzoneField,
                      maxFiles: 1,
                      uploadRequest: uploadCompanyLogo,
                      deleteRequest: deleteCompanyLogo,
                      accept: 'image/*',
                    }}
                  />
                  {!!acceptedListingContacts.length && (
                    <ul>
                      {acceptedListingContacts.map(
                        ({
                          email,
                          name,
                          position,
                          linkedinProfile,
                          brokerImage,
                        }) => (
                          <li key={email}>
                            <div className="res-lc-content">
                              <span>{name}</span>
                              <span>{position}</span>
                              {linkedinProfile ? (
                                <a href={linkedinProfile} {...NEW_WINDOW_PROPS}>
                                  <LinkedInIcon />
                                  {t('linkedInProfile')}
                                </a>
                              ) : (
                                <span />
                              )}
                            </div>
                            <ResolveImage src={brokerImage?.url} />
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                  {ActionsPlaceholder}
                </div>
              </aside>
            </div>
            <FloatingActions
              message={t('formReadyToSubmit')}
              cancelTxt={t('back')}
              submitTxt={t('saveAndContinue')}
              onCancel={() =>
                navigate(
                  getCreateListingRoute(
                    dealRoomId,
                    listingId,
                    listingType,
                    isEdit,
                  ),
                )
              }
            />
          </form>
        );
      }}
    />
  );
};

export default ListingDetailsPreview;
